/**
 * Who this client is, in one screenful.
 *
 * The open-matter count is read from the matters list rather than from a counter on the
 * payload, because there is no counter on the payload and inventing one client-side
 * would be a figure nobody could reconcile. Cursor pagination has no total, so when
 * there is another page the count is rendered as "25+" rather than as a number that is
 * quietly wrong.
 *
 * The count is also SCOPED: it is the matters this reader may see, not the matters that
 * exist. That is the same rule every list on this screen follows, and it is why the
 * label says so.
 */

import { STAFF, qk, usePaginatedList, type CaseListItem, type ClientDetail } from '@legalcrm/api';
import { Badge, Button, Card, CardBody, CardHeader, StatusPill } from '@legalcrm/ui';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { Can } from '@/auth/permissions';
import { useAuth } from '@/auth/AuthProvider';
import { CustomFieldsSummary } from '@/components/customFields/CustomFields';
import { formatDate, humanise } from '@/lib/format';

import { CLIENT_STATUS_TONES } from './ClientDetailPage';
import { DraftEngagementDialog } from './DraftEngagementDialog';
import { ConflictCheckPanel } from '../cases/ConflictCheckPanel';
import { useFirmMembers } from '../settings/useFirmMembers';

export function ClientSummaryTab({ client }: { client: ClientDetail }) {
  const { matterLabel } = useAuth();
  const members = useFirmMembers();

  const openMatters = usePaginatedList<CaseListItem>(
    qk.sub('clients', client.id, 'open-matters'),
    STAFF.cases,
    { client: client.id, status: 'open', limit: 100 },
  );

  const openCount = openMatters.isLoading
    ? '—'
    : `${openMatters.items.length}${openMatters.hasNext ? '+' : ''}`;

  const primary = client.contacts.find((contact) => contact.is_primary);
  const profile = client.profile;

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      {/* Full width and above everything, because it is the reason somebody cannot get
          on with the work. Absent entirely once both are done. */}
      <div className="lg:col-span-3">
        <BeforeOpeningAMatter client={client} matterLabel={matterLabel} />
      </div>

      <Card className="lg:col-span-2">
        <CardHeader title="The relationship" />
        <CardBody>
          <dl className="grid gap-3 sm:grid-cols-2">
            <Row label="Name">{client.display_name}</Row>
            <Row label="Reference">
              <span className="font-mono">{client.reference}</span>
            </Row>
            <Row label="Type">
              {client.client_type === 'organization' ? 'Organisation' : 'Individual'}
            </Row>
            <Row label="Status">
              <StatusPill
                label={humanise(client.status)}
                tone={CLIENT_STATUS_TONES[client.status] ?? 'neutral'}
              />
            </Row>
            <Row label="Responsible lawyer">
              {client.responsible_lawyer ? (
                members.nameFor(client.responsible_lawyer)
              ) : (
                <Badge tone="warning" size="sm">
                  Unassigned
                </Badge>
              )}
            </Row>
            <Row label="Owner">{members.nameFor(client.owner_user)}</Row>
            <Row label={`Open ${matterLabel.toLowerCase()}s you can see`}>
              <span className="tabular-nums">{openCount}</span>
            </Row>
            <Row label="Primary contact">
              {primary ? (
                primary.full_name
              ) : (
                <Badge tone="warning" size="sm">
                  None designated
                </Badge>
              )}
            </Row>
            <Row label="On file since">{formatDate(client.created_at)}</Row>
          </dl>

          {/*
            The firm's own fields, below the ones every firm has. A value carrying a
            "Client can read" label is worth seeing next to the relationship rather than
            buried, because that is the one somebody should think about before typing
            into it again.

            Renders nothing when the firm has added none.
          */}
          <div className="mt-3">
            <CustomFieldsSummary entity="client" values={client.custom_fields} />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Details"
          description={profile ? undefined : 'Nothing recorded beyond the name.'}
        />
        <CardBody>
          {profile ? (
            <dl className="flex flex-col gap-3">
              <Row label="Date of birth">{formatDate(profile.date_of_birth)}</Row>
              <Row label="Nationality">{profile.nationality || '—'}</Row>
              {/* The identity number itself is held encrypted and appears on no staff
                  serializer. The last four digits are all this surface can express, and
                  they exist to confirm the right person — not to reconstruct a number. */}
              <Row label="Identity number">
                {profile.national_id_last4 ? `•••• ${profile.national_id_last4}` : '—'}
              </Row>
              <Row label="Address">
                {[
                  profile.address_line1,
                  profile.address_line2,
                  profile.city,
                  profile.postal_code,
                  profile.country,
                ]
                  .filter(Boolean)
                  .join(', ') || '—'}
              </Row>
              {client.client_type === 'organization' && (
                <>
                  <Row label="Company number">{profile.company_registration_no || '—'}</Row>
                  <Row label="Tax id">{profile.tax_id || '—'}</Row>
                </>
              )}
            </dl>
          ) : (
            <p className="text-sm text-ink-subtle">
              No identity or address detail has been captured for this client.
            </p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * What is still missing before a matter can be opened for this client.
 *
 * ===========================================================================
 * THE REFUSAL, MOVED TO WHERE SOMEBODY CAN ACT ON IT
 * ===========================================================================
 * A client created directly — the walk-in, the referral, the relationship migrated from
 * another system — skips both the conflict check and the engagement letter. `open_case`
 * refuses without a decided check, and the service's work gate locks work without a
 * signed letter. Both refusals are right, and both used to appear for the first time at
 * the moment somebody pressed "open a matter", naming a step with no button anywhere
 * near it.
 *
 * So the two gates are reported here, each with the thing that satisfies it. Neither
 * blocks anything on this screen: a firm may hold a client record for somebody it has
 * not yet engaged, and creating one is deliberately never refused for either reason.
 *
 * RENDERS NOTHING when both are done. A panel that stays and says "all clear" is a panel
 * people stop reading, and the one time it matters they will not see it.
 */
function BeforeOpeningAMatter({
  client,
  matterLabel,
}: {
  client: ClientDetail;
  matterLabel: string;
}) {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [drafting, setDrafting] = useState(false);

  const readiness = client.engagement_readiness;
  const conflict = readiness.conflict_check;
  const engagement = readiness.engagement_letter;

  if (readiness.is_ready) return null;

  return (
    <Card>
      <CardHeader
        title={`Before opening a ${matterLabel.toLowerCase()}`}
        description={`This client is missing something a ${matterLabel.toLowerCase()} needs. Neither stops you keeping the record.`}
      />
      <CardBody className="flex flex-col gap-3">
        {!conflict.satisfied && (
          <div className="flex flex-wrap items-start justify-between gap-3 rounded border border-warning/30 bg-warning-subtle px-3 py-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink">No decided conflict check</p>
              {/* The gate's own wording, so this and the eventual refusal read the same. */}
              <p className="text-sm text-ink-muted">{conflict.reason}</p>
            </div>
            {!checking && (
              // `conflict.create` runs the search; deciding needs `conflict.approve`, and
              // the panel itself is what asks for that. Somebody who may run one and not
              // decide it still gets the search started, which is their half of the job.
              <Can module="conflict" action="create">
                <Button variant="primary" size="sm" onClick={() => setChecking(true)}>
                  Run conflict check
                </Button>
              </Can>
            )}
          </div>
        )}

        {checking && !conflict.satisfied && (
          // The same panel the matter screen and the lead screen use, pointed at this
          // client. Running the check where the gap is noticed beats sending somebody
          // away to find a feature and back again to retype what they had.
          <ConflictCheckPanel
            subjectType="client"
            subjectId={client.id}
            subjectName={client.display_name}
            requirementLabel={`opening a ${matterLabel.toLowerCase()}`}
          />
        )}

        {!engagement.satisfied && (
          <div className="flex flex-wrap items-start justify-between gap-3 rounded border border-warning/30 bg-warning-subtle px-3 py-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink">No signed engagement letter</p>
              <p className="text-sm text-ink-muted">
                {engagement.agreement_id
                  ? engagement.agreement_status === 'draft'
                    ? 'A letter has been drafted and never issued, so nothing has reached the client yet.'
                    : 'A letter is out for signature and has not been signed yet.'
                  : engagement.reason}
              </p>
            </div>
            {engagement.agreement_id ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/agreements/${engagement.agreement_id}`)}
              >
                {engagement.agreement_status === 'draft' ? 'Open the draft' : 'Open the letter'}
              </Button>
            ) : (
              <Can module="agreement" action="create">
                <Button variant="primary" size="sm" onClick={() => setDrafting(true)}>
                  Draft engagement letter
                </Button>
              </Can>
            )}
          </div>
        )}

        <DraftEngagementDialog
          client={client}
          open={drafting}
          onClose={() => setDrafting(false)}
        />

        {/* Plain text, not a link. The tab is in the bar directly above this card, and a
            link that only changes the query string would leave the reader on the same
            panel wondering what it did. */}
        <p className="text-xs text-ink-subtle">
          Every letter for this client is on the Agreements tab.
        </p>
      </CardBody>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd className="mt-0.5 break-words text-base text-ink">{children}</dd>
    </div>
  );
}
